// src/utils/ukTaxUi.js

export function isUkCountry(country = "") {
  const c = String(country || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");

  return c === "UK" || c === "GB" || c === "UNITED_KINGDOM";
}

export function normalizeUkMatchingRule(rule = "") {
  const r = String(rule || "").trim().toLowerCase();

  if (r === "same_day" || r === "same-day") return "same_day";
  if (
    r === "bed_and_breakfast_30d" ||
    r === "bed-and-breakfast-30d" ||
    r === "30_day" ||
    r === "30-day"
  ) {
    return "bed_and_breakfast_30d";
  }
  if (
    r === "section_104" ||
    r === "section104" ||
    r === "section_104_pool" ||
    r === "s104"
  ) {
    return "section_104";
  }
  if (r === "uk_nft_identifiable" || r === "nft_identifiable") {
    return "uk_nft_identifiable";
  }

  return r;
}

export function getUkMatchingRule(row = {}) {
  const direct =
    row.matchingRule ||
    row.ukMatchingRule ||
    row.matching_rule ||
    "";

  if (direct) return normalizeUkMatchingRule(direct);

  if (Array.isArray(row.matchingRules) && row.matchingRules.length) {
    return row.matchingRules.map(normalizeUkMatchingRule).join("+");
  }

  const method = String(row.costBasisMethod || row.ukMethod || "").toUpperCase();

  if (method === "UK_NFT_IDENTIFIABLE") return "uk_nft_identifiable";
  if (method === "UK_S104_POOLING") return "section_104";

  return "";
}

export function isUkS104Row(row = {}) {
  if (!row) return false;

  const method = String(
    row.costBasisMethod ||
    row.ukMethod ||
    ""
  ).toUpperCase();

  if (method === "UK_S104_POOLING") {
    return true;
  }

  const rules = String(
    getUkMatchingRule(row) || ""
  )
    .split("+")
    .map(normalizeUkMatchingRule)
    .filter(Boolean);

  return rules.some((rule) =>
    [
      "same_day",
      "bed_and_breakfast_30d",
      "section_104",
    ].includes(rule)
  );
}

export function isUkNftIdentifiableRow(row = {}) {
  if (!row) return false;

  const method = String(
    row.costBasisMethod ||
    row.ukMethod ||
    ""
  ).toUpperCase();

  if (method === "UK_NFT_IDENTIFIABLE") {
    return true;
  }

  const rules = String(
    getUkMatchingRule(row) || ""
  )
    .split("+")
    .map(normalizeUkMatchingRule)
    .filter(Boolean);

  return rules.includes(
    "uk_nft_identifiable"
  );
}

export function formatUkMatchingRule(
  rule = "",
  language = "de"
) {
  const normalized =
    normalizeUkMatchingRule(rule);

  if (!normalized) {
    return "";
  }

  const labels = language === "en"
    ? {
        same_day: "Same-day",
        bed_and_breakfast_30d: "30-day",
        section_104: "Section 104 pool",
        uk_nft_identifiable: "NFT identifiable",
      }
    : {
        same_day: "Same-day",
        bed_and_breakfast_30d: "30-day",
        section_104: "Section 104 Pool",
        uk_nft_identifiable: "NFT identifiable",
      };

  return labels[normalized] || String(rule);
}

export function isUkNftMintPaymentClassification(row = {}) {
  const txCls = String(
    row?.classification || row?.type || row?.event || row?.kind || ""
  ).toLowerCase();

  return (
    txCls.includes("nft_mint") ||
    txCls.includes("nft_galaxy") ||
    txCls.includes("galaxy_mint") ||
    txCls === "mint" ||
    txCls === "mint_payment" ||
    txCls.includes("pending_mint_payment")
  );
}

export function formatUkLegMatchingLabel(leg = {}, parentTx = {}, language = "de") {
  const legRule = getUkMatchingRule(leg) || getUkMatchingRule(parentTx);
  const isUK =
    isUkCountry(parentTx?.selectedCountry) ||
    String(parentTx?.costBasisMethod || leg?.costBasisMethod || parentTx?.ukMethod || "")
      .toUpperCase() === "UK_S104_POOLING" ||
    String(leg?.costBasisMethod || "").toUpperCase() === "UK_S104_POOLING" ||
    Boolean(legRule);

  const isNftMintPayment = isUkNftMintPaymentClassification(parentTx);
  const method = String(
    parentTx?.costBasisMethod ||
      leg?.costBasisMethod ||
      parentTx?.ukMethod ||
      (legRule ? "UK_S104_POOLING" : "")
  ).toUpperCase();

  if (
    isUK &&
    isNftMintPayment &&
    (method === "UK_S104_POOLING" || Boolean(legRule)) &&
    normalizeUkMatchingRule(legRule) !== "uk_nft_identifiable"
  ) {
    return language === "en" ? "Payment-token S104" : "Zahlungstoken: Section 104";
  }

  return formatUkMatchingRule(legRule, language);
}

/** @deprecated Import from ../utils/taxAssignmentLabels.js */
export {
  shouldUseUkAssignmentLabels,
  getAssignmentTitle,
  getSaleDetailTitle,
} from "./taxAssignmentLabels.js";

/** Kollabiert direkte + inflowsByToken-Alias-Keys, erhält echte hash#eventIndex-Keys. */
export function normalizeUkIncomeEventKeyForDedup(eventKey = "", hash = "") {
  const key = String(eventKey || "").trim().toLowerCase();
  const h = String(hash || "").trim().toLowerCase();

  if (!key) return h || "";
  if (!h) return key;

  // Normal: hash#2 bleibt hash#2
  if (key.startsWith(`${h}#`) && !key.startsWith(`${h}#inflowsbytoken:`)) {
    return key;
  }

  // Duplicate source: hash#inflowsByToken:...|hash|cls|token|ts|eventIndex|...
  if (key.startsWith(`${h}#inflowsbytoken:`)) {
    const payload = key.slice(`${h}#inflowsbytoken:`.length);
    const parts = payload.split("|");

    const eventIndex = parts[5];

    if (eventIndex !== undefined && eventIndex !== "" && eventIndex !== "null") {
      return `${h}#${eventIndex}`;
    }

    return h;
  }

  // Bare S104 eventId: inflowsbytoken:b3tr:2379|hash|cls|token|ts|clause|eventIdx|index
  if (key.startsWith("inflowsbytoken:")) {
    const parts = key.split("|");
    const aliasHash = String(parts[1] || h || "").trim().toLowerCase();
    const eventIndex = parts[5];
    if (aliasHash && eventIndex !== undefined && eventIndex !== "" && eventIndex !== "null") {
      return `${aliasHash}#${eventIndex}`;
    }
    return aliasHash || key;
  }

  return key;
}

/** Native VET/B3TR rows often share event_index 0 — not unique enough for capital dedupe. */
export function isAmbiguousUkEventUniq(eventUniq = "") {
  const s = String(eventUniq ?? "").trim();
  if (!s) return true;
  if (s.includes("|")) return false;
  if (/^\d+$/.test(s) && Number(s) === 0) return true;
  return false;
}

export function resolveUkCapitalEventKey({
  hash = "",
  eventUniq = "",
  rowSeq = 0,
  eventId = "",
} = {}) {
  const hashLc = String(hash || "").trim().toLowerCase();
  const stableEventId = String(eventId || "").trim();

  if (stableEventId.includes("|")) {
    return stableEventId.toLowerCase();
  }

  if (!isAmbiguousUkEventUniq(eventUniq)) {
    return `${hashLc}#${String(eventUniq).toLowerCase()}`;
  }

  return `${hashLc}#row${rowSeq}`;
}

/** Same Event_Key resolution as ukFinalExport capital/review rows. */
export function makeUkFinalEventKeyFromTaxRow(row = {}, { rowSeq = 0, syntheticSource = "" } = {}) {
  const hash0 = String(row?.hash || row?.txid || row?.event_id || row?.txHash || "").trim();
  const hash = hash0 || (syntheticSource ? `synthetic:${syntheticSource}:${rowSeq}` : "");
  if (!hash) return "";

  return resolveUkCapitalEventKey({
    hash,
    eventUniq: pickUkEventUniq(row),
    rowSeq,
    eventId: row?.eventId || row?.exportEventKey || row?.eventKey || "",
  }).toLowerCase();
}

export function pickUkEventUniq(row = {}) {
  const clauseRaw =
    row?.clauseIndex ??
    row?.clause_index ??
    row?.raw_json?.clause_index ??
    row?.raw_json?.clauseIndex ??
    null;
  const eventRaw =
    row?.eventIndex ??
    row?.event_index ??
    row?.raw_json?.event_index ??
    row?.raw_json?.eventIndex ??
    null;
  const logRaw =
    row?.logIndex ??
    row?.log_index ??
    row?.raw_json?.log_index ??
    row?.raw_json?.logIndex ??
    null;

  const clause =
    clauseRaw === null || clauseRaw === undefined || clauseRaw === ""
      ? null
      : Number(clauseRaw);
  const event =
    eventRaw === null || eventRaw === undefined || eventRaw === ""
      ? null
      : Number(eventRaw);
  const log =
    logRaw === null || logRaw === undefined || logRaw === ""
      ? null
      : Number(logRaw);

  // Multi-clause txs share eventIndex — clause must be part of the identity.
  if (Number.isInteger(clause) && Number.isInteger(event)) {
    let key = `c${clause}:e${event}`;
    if (Number.isInteger(log) && log !== event) key += `:l${log}`;
    return key;
  }

  const v =
    row?.eventRef ??
    row?.event_ref ??
    row?.eventReference ??
    row?.event_reference ??
    row?.logIndex ??
    row?.log_index ??
    row?.eventIndex ??
    row?.event_index ??
    row?.transferIndex ??
    row?.transfer_index ??
    row?.clauseIndex ??
    row?.clause_index ??
    row?.raw_json?.event_index ??
    row?.raw_json?.eventIndex ??
    row?.raw_json?.log_index ??
    row?.raw_json?.logIndex ??
    row?.raw_json?.clause_index ??
    row?.raw_json?.clauseIndex ??
    row?.exportEventKey ??
    row?.eventKey ??
    row?.uniqueEventKey ??
    row?.rowKey ??
    row?.eventId ??
    row?.event_id ??
    row?.subIndex ??
    row?.sub_index ??
    row?.idx ??
    row?.index ??
    "";

  return v === "" || v == null ? "" : String(v);
}

export function hasUsableUkFinalMoney(row = {}) {
  const values = [
    row.taxableProfitFiat,
    row.taxableProfitEUR,
    row.taxableProfit,
    row.grossProfitFiat,
    row.grossProfitEUR,
    row.profitFiat,
    row.profitEUR,
    row.amountFiat,
    row.euroAmount,
    row.totalFiat,
    row.totalEUR,
    row.grossSellFiat,
    row.grossSellEUR,
    row.grossBuyFiat,
    row.grossBuyEUR,
  ];

  return values.some((v) => {
    if (v === null || v === undefined || v === "") return false;
    const n = Number(v);
    return Number.isFinite(n);
  });
}
